import React, { useState } from "react";
import { Flex, Form, Modal, Radio, Spin, Typography, message } from "antd";
import {
  WsProduct,
  WsProductExportFormat,
  WsProductExportKind,
  WsProductLinkScope,
} from "../../../api/webshop/wsProductsApi";
import {
  buildWsProductLinkRows,
  downloadWsProductLinkExport,
} from "./exportWsProducts";

const { Text } = Typography;

interface ExportProductsModalProps {
  open: boolean;
  onClose: () => void;
  products: WsProduct[];
}

interface ExportFormValues {
  exportKind: WsProductExportKind;
  linkScope: WsProductLinkScope;
  format: WsProductExportFormat;
}

export default function ExportProductsModal({
  open,
  onClose,
  products,
}: ExportProductsModalProps) {
  const [form] = Form.useForm<ExportFormValues>();
  const [isExporting, setIsExporting] = useState(false);
  const exportKind = Form.useWatch("exportKind", form) || "LINKS";

  const handleFinish = async (values: ExportFormValues) => {
    if (!products.length) {
      message.warning("No products available to export");
      return;
    }

    setIsExporting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 50));

      if (values.exportKind === "LINKS") {
        const rows = buildWsProductLinkRows(products, values.linkScope);
        downloadWsProductLinkExport(rows, values.format);
        message.success(`Exported ${rows.length} link row(s)`);
      }

      onClose();
    } catch (err) {
      console.error("Product export failed:", err);
      message.error("Failed to export products");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal
      title="Export Products"
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Export"
      confirmLoading={isExporting}
      maskClosable={!isExporting}
      closable={!isExporting}
      okButtonProps={{
        disabled: isExporting || products.length === 0,
        style: {
          backgroundColor: "rgb(255, 70, 37)",
          borderColor: "rgb(255, 70, 37)",
        },
      }}
      cancelButtonProps={{ disabled: isExporting }}
      destroyOnClose
      width={520}
    >
      <Spin spinning={isExporting} tip="Preparing export...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            exportKind: "LINKS",
            linkScope: "ALL_COUNTRIES",
            format: "CSV",
          }}
          style={{ paddingTop: 8 }}
        >
          <Form.Item
            label="Export"
            name="exportKind"
            tooltip="More export types can be added here later"
          >
            <Radio.Group>
              <Radio value="LINKS">Links</Radio>
            </Radio.Group>
          </Form.Item>

          {exportKind === "LINKS" && (
            <Form.Item label="Link" name="linkScope">
              <Radio.Group>
                <Radio value="ALL_COUNTRIES">All countries</Radio>
                <Radio value="ONE_PER_PRODUCT">Only 1 link per product</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item label="Format" name="format">
            <Radio.Group>
              <Radio value="CSV">CSV</Radio>
              <Radio value="EXCEL">Excel</Radio>
            </Radio.Group>
          </Form.Item>

          <Flex vertical gap={4}>
            <Text type="secondary">
              Columns: productTitle, productName, countryName, productLink
            </Text>
            <Text type="secondary">
              {products.length} product(s) will be included.
            </Text>
          </Flex>
        </Form>
      </Spin>
    </Modal>
  );
}
